import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  // "checking" | "valid" | "expired" | "used" | "not_found" | "invalid"
  const [tokenState, setTokenState] = useState("checking");

  useEffect(() => {
    if (!token) {
      setTokenState("invalid");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/auth/validate-reset-token/${encodeURIComponent(token)}`);
        if (cancelled) return;
        if (data?.valid) {
          setTokenState("valid");
        } else {
          const reason = data?.reason || "invalid";
          setTokenState(["expired", "used", "not_found"].includes(reason) ? reason : "invalid");
        }
      } catch {
        if (!cancelled) setTokenState("invalid");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("A palavra-passe deve ter pelo menos 6 caracteres");
      return;
    }
    if (password !== confirm) {
      toast.error("As palavras-passe não coincidem");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Palavra-passe atualizada");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao redefinir");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#F9F6F0]">
      <div className="md:w-1/2 relative bg-[#3B2B23] text-[#F9F6F0] p-6 sm:p-10 md:p-12 lg:p-16 flex flex-col justify-between overflow-hidden min-h-[180px] md:min-h-screen">
        <div className="grain absolute inset-0 opacity-30 pointer-events-none" />
        <div className="relative z-10 flex items-center gap-3 sm:gap-4">
          <img src="/unlimited-logo.png" alt="Unlimited Studio" className="w-10 h-10 sm:w-14 sm:h-14 object-contain bg-[#F9F6F0] rounded-full p-1.5 sm:p-2" />
          <div>
            <div className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#C07C5B]">Unlimited Studio</div>
            <div className="text-[9px] sm:text-[10px] tracking-wider text-[#E6DED6]/70 mt-0.5 sm:mt-1">Strategic Branding &amp; Communication</div>
          </div>
        </div>
        <div className="relative z-10 fade-up hidden md:block">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-tight">
            Defino uma<br />
            <span className="italic text-[#C07C5B]">nova palavra-passe.</span>
          </h1>
        </div>
        <div className="relative z-10 text-[10px] tracking-wider text-[#E6DED6]/60 uppercase hidden md:block">
          © {new Date().getFullYear()} — Unlimited Studio
        </div>
      </div>

      <div className="md:w-1/2 flex items-center justify-center p-6 sm:p-10 md:p-12 lg:p-16 flex-1">
        <div className="w-full max-w-md fade-up">
          {tokenState === "checking" ? (
            <div className="text-center text-[#5C4A43] text-xs uppercase tracking-widest">A validar o link…</div>
          ) : tokenState !== "valid" && !done ? (
            <div className="p-6 rounded-2xl border border-[#F3D2CE] bg-[#FBE6E4]" data-testid="reset-invalid">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#8A2A22] mb-2">Link inválido</div>
              <h2 className="text-2xl text-[#1A1412]">
                {tokenState === "expired" ? "Este link expirou" :
                 tokenState === "used" ? "Este link já foi utilizado" :
                 "Link inválido ou desconhecido"}
              </h2>
              <p className="mt-3 text-sm text-[#5C4A43] font-light">
                Os links de recuperação são válidos durante 1 hora e podem ser usados apenas uma vez.
                Peço um novo link para prosseguir.
              </p>
              <Button
                onClick={() => navigate("/forgot-password")}
                className="mt-6 w-full h-12 bg-[#3B2B23] hover:bg-[#2A1E18] text-[#F9F6F0] rounded-full text-xs tracking-[0.3em] uppercase"
                data-testid="request-new-link-button"
              >
                Pedir um novo link
              </Button>
              <div className="text-center mt-3">
                <Link to="/login" className="text-[11px] uppercase tracking-[0.2em] text-[#8B5E3C] hover:text-[#3B2B23]">Voltar ao login</Link>
              </div>
            </div>
          ) : done ? (
            <div className="p-6 rounded-2xl border border-[#EAE3DB] bg-white" data-testid="reset-success">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#2C543B] mb-2">Tudo pronto</div>
              <h2 className="text-2xl text-[#1A1412]">A minha palavra-passe foi atualizada</h2>
              <p className="mt-3 text-sm text-[#5C4A43] font-light">
                Já posso entrar com as novas credenciais.
              </p>
              <Button
                onClick={() => navigate("/login")}
                className="mt-6 w-full h-12 bg-[#3B2B23] hover:bg-[#2A1E18] text-[#F9F6F0] rounded-full text-xs tracking-[0.3em] uppercase"
                data-testid="reset-go-login-button"
              >
                Entrar
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="reset-form">
              <h2 className="text-3xl sm:text-4xl text-[#1A1412]">Nova palavra-passe</h2>
              <p className="mt-3 text-[#5C4A43] text-sm font-light">
                Defino uma palavra-passe segura com pelo menos 6 caracteres.
              </p>
              <div className="mt-10 space-y-5">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Nova palavra-passe</Label>
                  <Input
                    required type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="mt-2 h-12 bg-white border-[#EAE3DB] focus-visible:ring-[#8B5E3C]"
                    data-testid="reset-password-input"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Confirmar</Label>
                  <Input
                    required type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="••••••••"
                    className="mt-2 h-12 bg-white border-[#EAE3DB] focus-visible:ring-[#8B5E3C]"
                    data-testid="reset-confirm-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-[#3B2B23] hover:bg-[#2A1E18] text-[#F9F6F0] rounded-full text-xs tracking-[0.3em] uppercase"
                  data-testid="reset-submit-button"
                >
                  {loading ? "A guardar…" : "Definir palavra-passe"}
                </Button>
                <div className="text-center">
                  <Link
                    to="/login"
                    className="text-[11px] uppercase tracking-[0.2em] text-[#8B5E3C] hover:text-[#3B2B23]"
                  >
                    Cancelar
                  </Link>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
